import { Typography } from '@euler/app/components/typography';
import { canManageApdmImports } from '@euler/functions/apdmAccess';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import {
  ApdmCouponCampaignStatus,
  ApdmCouponCampaignSummary,
} from '@euler/model/apdm';
import { parseApdmCouponWidgetPayload } from '@euler/model/apdm/widgets';
import { useServiceFactory } from '@euler/services/factory';
import { CircleCheck, Ticket } from 'lucide-react-native';
import { memo, useEffect, useState } from 'react';
import { View } from 'react-native';
import {
  CardLoading,
  CardNotice,
  ChatCard,
  ChatCardBadge,
} from '../components/ChatCard';
import {
  couponItemText,
  couponPeriodText,
  couponStockText,
  kCouponStatusLabels,
} from '../components/couponText';
import { aiColors } from '../theme';
import { CouponCampaignDetailSheet } from './CouponCampaignDetailSheet';

/** The coupon campaign a marketing draft now carries, e.g. one just built. */
export const CouponCampaignCard = memo(
  ({ data, streaming }: { data: unknown; streaming?: boolean }) => {
    const profile = useUserProfileInfo();
    const ref = parseApdmCouponWidgetPayload(data);
    if (streaming) return null;
    if (!ref) return <CardNotice text="这张卡片的引用无效。" />;
    if (!canManageApdmImports(profile ?? null))
      return <CardNotice text="当前账号无法查看这个优惠券活动。" />;
    return (
      <CouponCampaignBody key={ref.campaignId} campaignId={ref.campaignId} />
    );
  },
);

function CouponCampaignBody({ campaignId }: { campaignId: string }) {
  const { rpx } = useResponsiveContext();
  const { apdmCampaignService } = useServiceFactory();
  const [coupon, setCoupon] = useState<
    ApdmCouponCampaignSummary | null | undefined
  >(undefined);
  const [failed, setFailed] = useState(false);
  const [detail, setDetail] = useState(false);
  useEffect(() => {
    let active = true;
    apdmCampaignService
      .coupon({ campaignId })
      .then(value => {
        if (active) setCoupon(value);
      })
      .catch(error => {
        console.error('APDM campaign coupon failed', error);
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [apdmCampaignService, campaignId]);
  if (failed)
    return (
      <ChatCard icon={Ticket} title="优惠券活动">
        <CardNotice tone="danger" text="优惠券活动没有加载出来，请稍后重试。" />
      </ChatCard>
    );
  if (coupon === undefined)
    return (
      <ChatCard icon={Ticket} title="优惠券活动">
        <CardLoading label="正在读取优惠券活动…" />
      </ChatCard>
    );
  if (coupon === null)
    return (
      <ChatCard
        icon={Ticket}
        title="活动当前没有优惠券"
        subtitle="这个活动已改为不带优惠券发送。"
      />
    );
  const unpublished = coupon.status === ApdmCouponCampaignStatus.Draft;
  const line = (label: string, value: string) => (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: rpx(12),
      }}
    >
      <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
        {label}
      </Typography.Text>
      <Typography.Text
        medium
        size={rpx(12)}
        color={aiColors.foreground}
        style={{ flexShrink: 1, textAlign: 'right' }}
      >
        {value}
      </Typography.Text>
    </View>
  );
  return (
    <ChatCard
      icon={CircleCheck}
      color={aiColors.green}
      title={coupon.title}
      subtitle={unpublished ? '已创建 · 启动活动时一并发布' : '已就绪'}
      badge={
        <ChatCardBadge
          label={kCouponStatusLabels[coupon.status]}
          color={unpublished ? aiColors.amber : aiColors.green}
        />
      }
      detail={{ label: '查看优惠券详情', onPress: () => setDetail(true) }}
    >
      <View
        style={{
          gap: rpx(8),
          paddingTop: rpx(10),
          borderTopWidth: 1,
          borderTopColor: aiColors.border,
        }}
      >
        <View style={{ gap: rpx(2) }}>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            权益
          </Typography.Text>
          {coupon.items.map((item, index) => (
            <Typography.Text
              key={index}
              medium
              size={rpx(12)}
              color={aiColors.foreground}
            >
              {couponItemText(item)}
            </Typography.Text>
          ))}
        </View>
        {line('有效期', couponPeriodText(coupon))}
        {line('库存', couponStockText(coupon))}
      </View>
      <CouponCampaignDetailSheet
        coupon={detail ? coupon : null}
        onClose={() => setDetail(false)}
      />
    </ChatCard>
  );
}
